import { Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { GlobalMailTemplate, MailTemplateVars } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import {
  buildBookableWorkshopsHTML,
  buildConfirmMail,
  buildForgotPassword,
  buildLoginLink,
  buildOTP,
  buildProductCard,
  buildProductChangesHtml,
  buildRegistrationsHTML,
  type BuildProductChanges,
} from '@adornis/buchungsheld-db/mails/mail-helpers.js';
import { sdTimeSlot } from '@adornis/buchungsheld-db/plugins/payment/sd-payment-plugin.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { xComponents } from '@adornis/chemistry/elements/x-components.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XMailEditor } from '@adornis/mails/client/x-mail-editor.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';

const mockAppointments = [
  new Appointment({
    _id: '1',
    name: 'Introduction',
    start: DateTime.now().plus({ days: 1 }),
    end: DateTime.now().plus({ days: 3 }),
    location: 'Uniplatz',
  }),
  new Appointment({
    _id: '2',
    start: DateTime.now().set({ hour: 8 }).plus({ days: 5 }).startOf('hour'),
    end: DateTime.now().set({ hour: 12 }).plus({ days: 8 }).startOf('hour'),
    location: 'Hoersaal',
  }),
];

/**
 * @element tr-mail-editor
 */
@customElement('tr-mail-editor')
export class TrMailEditor extends XMailEditor {
  @property({ type: Boolean }) overriding?: boolean;

  @state() private readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  protected editorOptions = {
    toolbar: this.tinyEditorOptions,
    relative_urls: false,
    remove_script_host: false,
  };

  override async getVariableValues() {
    const mailTemplate = this.value.value;
    if (!mailTemplate) return {};
    const superVarValues = await super.getVariableValues();

    const changedRegistrationsValue = buildRegistrationsHTML(
      [
        {
          name: 'Workshop Name',
          state: ParticipationState.WAITING,
          topic: 'Test Theme',
          language: 'de',
          waitingListPlace: 2,
          institution: new Institution({}),
          appointments: mockAppointments,
        },
      ],
      true,
    );

    const changedRegistrationsEnglishValue = buildRegistrationsHTML(
      [
        {
          name: 'Workshop Name',
          state: ParticipationState.WAITING,
          topic: 'Test Theme',
          language: 'de',
          waitingListPlace: 2,
          institution: new Institution({}),
          appointments: mockAppointments,
          englishVersion: true,
        },
      ],
      true,
    );

    const oldWorkshop = new Workshop({
      name: 'Workshop 1',
      capacity: 10,
      infoLink: 'https://google.com',
      appointments: [
        {
          _id: 'appointment1',
          name: 'Termin 1',
          start: DateTime.now(),
          end: DateTime.now(),
          location: 'Ort',
        },
        {
          _id: 'appointment2',
          name: 'Termin 2',
          start: DateTime.now(),
          end: DateTime.now(),
          location: 'Ort 2',
        },
      ],
    });

    const newWorkshop = {
      name: { old: 'Alter Titel', new: 'Neuer Titel' },
      teachings: {
        old: [{ title: 'alter Dozent', firstName: 'alter', lastName: 'Dozent' }],
        new: [{ title: 'neuer Dozent', firstName: 'neuer', lastName: 'Dozent' }],
      },
      appointments: [
        {
          old: { _id: 'appointment1', location: 'Alter Ort', start: DateTime.now() },
          new: {
            _id: 'appointment1',
            location: 'Neuer Ort',
            start: DateTime.now().plus({ days: 2 }),
          },
        },
        {
          old: {},
          new: {
            _id: 'appointment2',
            name: 'Termin 2',
          },
        },
        {
          new: {},
          old: {
            _id: 'appointment3',
            name: 'Termin 3',
            start: DateTime.now(),
            end: DateTime.now(),
            location: 'Ort',
          },
        },
      ],
    } as BuildProductChanges;

    return {
      ...superVarValues,
      ...Object.fromEntries(
        [
          [MailTemplateVars.FORGOT_PASSWORD_LINK, buildForgotPassword('https://www.google.de')],
          [MailTemplateVars.CONFIRM_MAIL_LINK, buildConfirmMail('https://www.google.de')],
          [MailTemplateVars.OTP, buildOTP('2456')],
          [MailTemplateVars.LOGIN_LINK, buildLoginLink('1298578215712895719282571')],
          [
            MailTemplateVars.PRODUCT_CARD,
            buildProductCard('Workshop Name', DateTime.now(), undefined, false, 'Hörsaal 2'),
          ],
          [MailTemplateVars.EVENT_LOCATION, 'Musterstraße 42, 123456 Musterort'],
          [MailTemplateVars.EVENT_DATES, '2025-03-26, 2025-03-26'],
          [MailTemplateVars.EVENT_LEAD_DAYS, '5'],
          [MailTemplateVars.PRODUCT_NAME, 'Workshop Name'],
          [MailTemplateVars.WORKSHOP_NAME, 'Workshop Name'],
          [
            MailTemplateVars.WORKSHOP_TERMINE,
            sdTimeSlot(DateTime.now().plus({ days: 10 }), DateTime.now().plus({ days: 10, hours: 2 })),
          ],
          [MailTemplateVars.WORKSHOP_LOCATION, 'Location1, Location2'],
          [MailTemplateVars.APPOINTMENT_NAME, 'Termin 1'],
          [MailTemplateVars.APPOINTMENT_START, DateTime.now().plus({ days: 10 })],
          [MailTemplateVars.APPOINTMENT_END, DateTime.now().plus({ days: 10, hours: 2 })],
          [MailTemplateVars.APPOINTMENT_LOCATION, 'Sachsenhügel Straße 42'],
          [MailTemplateVars.CHANGED_REGISTRATIONS, changedRegistrationsValue],
          [MailTemplateVars.CHANGED_REGISTRATIONS_EN, changedRegistrationsEnglishValue],
          [MailTemplateVars.EVENT_TITLE, 'Hallo-Welt Veranstaltung'],
          [
            MailTemplateVars.DOCUMENT_LINKS,
            '<a href="https://www.google.de">Link</a>, <a href="https://www.google.de">Link</a>',
          ],
          [
            MailTemplateVars.OTHER_REGISTRATIONS,
            buildRegistrationsHTML(
              [...new Array(3)].map((_, i) => ({
                name: `Workshop ${i + 1}`,
                state: ParticipationState.IN,
                topic: 'Test Theme',
                language: 'de',
                dates: [DateTime.now()],
                institution: new Institution({}),
                appointments: mockAppointments,
              })),
            ),
          ],
          [MailTemplateVars.PRODUCT_CHANGES, buildProductChangesHtml(oldWorkshop, newWorkshop, false)],
          [MailTemplateVars.PRODUCT_CHANGES_EN, buildProductChangesHtml(oldWorkshop, newWorkshop, true)],
          [
            MailTemplateVars.BOOKABLE_WORKSHOPS,
            buildBookableWorkshopsHTML(
              [1, 2, 3, 4, 5].map(
                i =>
                  new Workshop({
                    _id: 'testing_product_1',
                    institutionID: 'testing_institution_1',
                    name: 'Workshop ' + i,
                    appointments: [
                      {
                        name: 'Termin 1',
                        start: DateTime.now().plus({ day: i }),
                        end: DateTime.now().plus({ day: i, hour: 3 }),
                        location: 'online',
                      },
                    ],
                    Language: 'de',
                  }),
              ),
              false,
            ),
          ],
        ].filter(([key]) => key in superVarValues),
      ),
    };
  }

  protected override writerContent() {
    return xComponents(html`
      <x-flex space="md" flex>
        <x-h3> Nachricht </x-h3>
        ${this.variablesDisplay()}
        <x-input ${this._form.field('subject')} placeholder="Betreff"></x-input>
        <x-tinymce
          flex
          placeholder="Hier E-Mail Text eingeben.."
          .editorOptions=${this.editorOptions}
          ${this._form.field('content')}
        ></x-tinymce>
      </x-flex>
    `);
  }

  override render() {
    return html`
      <tr-flex space="lg" flex h100>
        <tr-flex horizontal space="md" wrap flex> ${this.editorContent()} ${this.previewContent()} </tr-flex>
        ${this.attachments ? this.attachmentSection() : nothing}
        <tr-flex start horizontal space="md">
          ${this.saveButton()} ${this.deleteButton()} ${this._copyFromGlobal()}
        </tr-flex>
      </tr-flex>
      ${this.attachmentSection()}
    `;
  }

  protected _copyFromGlobal() {
    if (!this.overriding) return nothing;
    const mailTemplate = this.value.value;
    if (!mailTemplate) return nothing;
    return html`
      <x-button
        mode="outline"
        icon="content_copy"
        @click=${() =>
          XDialog.guard(
            async () => {
              const globalMailTemplate = await GlobalMailTemplate.getMailTemplateByType(mailTemplate.emailType)(
                GlobalMailTemplate.allFields,
              );
              if (!globalMailTemplate) return;
              mailTemplate.subject = globalMailTemplate.subject;
              mailTemplate.content = globalMailTemplate.content;
            },
            {
              hasToConfirm: true,
              confirmText:
                'Wirklich lokale E-Mail-Vorlage mit globaler überschreiben? Die lokalen Änderungen gehen dabei verloren. Um die Änderungen zu persistieren, müssen Sie danach speichern.',
            },
          )}
      >
        ${this._translation.translate('MAIL_EDITOR', 'copy_from_template')}
      </x-button>
    `;
  }
}
